import PropTypes from 'prop-types';

import { FORMATTER_TYPES } from '../../utilities/FormattingUtilities';
import { COLUMN_TYPES } from './DataTableColumns';
import { ROWSIZE } from './DataTableConstants';
import { SELECTIONMODE_MULTI, SELECTIONMODE_NONE, SELECTIONMODE_SINGLE } from './SelectionContext';
import messageDescriptor from '../lingui/LinguiPropTypes';

export const StringOrTranslatedTitle = PropTypes.oneOfType([PropTypes.string, messageDescriptor, PropTypes.element]);

export const SimpleFormatter = PropTypes.oneOf(Object.values(FORMATTER_TYPES));

export const ExplicitFormatter = PropTypes.shape({
    type: SimpleFormatter.isRequired,
});

export const Formatter = PropTypes.oneOfType([SimpleFormatter, ExplicitFormatter, PropTypes.func]);

export const Link = PropTypes.oneOfType([
    PropTypes.shape({
        target: PropTypes.string.isRequired,
        mappedFilterParams: PropTypes.object,
        pageState: PropTypes.object,
        pageStateParams: PropTypes.arrayOf(PropTypes.string),
        excludedFilterParams: PropTypes.arrayOf(PropTypes.string),
    }),
    PropTypes.shape({
        clickHandler: PropTypes.func.isRequired,
    }),
    PropTypes.func,
]);

export const Icon = PropTypes.exact({
    icon: PropTypes.node,
    getIcon: PropTypes.func,
    clickHandler: PropTypes.func,
    getTooltip: PropTypes.func,
    tooltip: StringOrTranslatedTitle,
});

export const Icons = PropTypes.arrayOf(Icon);

export const Column = PropTypes.exact({
    /**
     * Id for the column, used for overrides
     */
    id: PropTypes.string,

    /**
     * Id of associated (primary) column, typically that column's
     * title. Used to "group" columns together when hiding/showing.
     */
    assocId: PropTypes.string,

    /**
     * The type of column. This applies base configurations to the
     * column so you don't have to specify them.
     */
    type: PropTypes.oneOf(Object.values(COLUMN_TYPES)),

    /**
     * The title to display for this column
     */
    title: StringOrTranslatedTitle,

    /**
     * The group name this column belongs to
     */
    group: messageDescriptor,

    /**
     * Whether or not this column can be resized.
     */
    resizable: PropTypes.bool,

    /**
     * Whether or not this column can be removed.
     */
    removable: PropTypes.bool,

    /**
     * Whether or not this column can be sorted. You must provide a value
     * for `sortProperty` to sort
     */
    sortable: PropTypes.bool,

    /**
     * The property this column uses to sort by. This is the value set for the
     * sorter array
     */
    sortProperty: PropTypes.string,

    /**
     * The string path of the value to retrieve for this column. Internally, this
     * uses `_.get()`.
     */
    mapping: PropTypes.string,

    /**
     * A callback function used to map the value for a column.
     *
     *     function mapper(columnConfig, rowData) {
     *         return someValue;
     *     }
     */
    mapper: PropTypes.func,

    /**
     * The width, in pixels, this column will occupy
     */
    width: PropTypes.number,

    /**
     * Flex-sizes this column's width. Flex are ratio-based, where the width of the column
     * is determined by the total available width of the table, divided by the ratio of the flex
     * of each column against the sum of all flex values from all columns. To prevent a column
     * from being too small, use `minWidth`.
     */
    flex: PropTypes.number,

    /**
     * The minimun witdh the column can be shrunk to when user resizes the column
     */
    minWidth: PropTypes.number,

    /**
     * The maximum witdh the column can be expanded to when user resizes the column
     */
    maxWidth: PropTypes.number,

    /**
     * The formatter this column will use to format the value to display
     */
    formatter: Formatter,

    /**
     * True if the data from the table should NOT be exportable to CSV
     */
    disableCSVExport: PropTypes.bool,

    /**
     * The formatter this column will use to format the value to display if the data is exported to CSV
     */
    csvFormatter: Formatter,

    /**
     * The title to display for csv export (only populate if different than title)
     */
    csvTitle: PropTypes.string,

    /**
     * True if the columns shouldn't be exported to CSV
     */
    csvIgnore: PropTypes.bool,

    /**
     * The alignment of the column header and value. Numbers are typically right-aligned,
     * text is left aligned, and icons are center aligned.
     */
    align: PropTypes.oneOf(['left', 'right', 'center']),

    /**
     * A custom renderer for this column. A renderer can return a string or a react element.
     */
    renderer: PropTypes.func,

    /**
     * The link configuration for link columns
     */
    link: Link,

    /**
     * Whether or not he column is hidden
     */
    hidden: PropTypes.bool,

    /**
     * The icon configuration for icon columns
     */
    icons: Icons,

    /**
     * `true` indicates this column should only appear in CSV exports.
     *
     * Defaults to: `false`
     */
    csvOnly: PropTypes.bool,

    /**
     * `true` indicates this column is only visible in the UI. It will
     * not appear in CSV exports or PDF reports.
     *
     * Defaults to: `false`
     */
    uiOnly: PropTypes.bool,

    /**
     * A css class that will be applied to the header cell
     */
    headerClassName: PropTypes.string,

    /**
     * A css class that will be applied to the cell
     */
    className: PropTypes.string,

    /**
     * A custom renderer for this column header. A renderer can return a string or a react element.
     */
    headerRenderer: PropTypes.func,

    /**
     * The link configuration for the link in the header
     */
    headerLink: Link,

    /**
     * Data to provide to the header link configuration.
     */
    headerLinkData: PropTypes.object,

    /**
     * Only used for the matrix to help us configure the sorting
     */
    statusSortField: PropTypes.string,

    /**
     * Only used for the matrix to help us configure the sorting
     */
    nameSortField: PropTypes.string,

    /**
     * The tooltip for the column header. This can be a string or a translated string.
     *
     * Defaults to: `null`
     */
    headerTooltip: StringOrTranslatedTitle,

    /**
     * A function to retrieve the tooltip for the column header. Used for procedurally generating
     * tooltips if necessary
     *
     * Defaults to: `(column) => column.headerTooltip`
     */
    getHeaderTooltip: PropTypes.func,

    /**
     * The tooltip configuration for displaying the value of a cell. This can either be a static
     * string or a boolean.
     *
     * If a `string`, this string will be displayed for all value cells.
     * If a `bool` with the value `true`, the tooltip wil display the value after being mapped and formatted
     *
     * Defaults to: `true`
     */
    tooltip: PropTypes.oneOfType([StringOrTranslatedTitle, PropTypes.bool]),

    /**
     * A function to retrieve the tooltip value for a column. This function is expected to return a string
     * or a translation object.
     *
     * Defaults to: See `DataTableColumns.jsx:defaultTooltipGetter`.
     */
    getTooltip: PropTypes.func,

    /**
     * A metric name used to determine the bar chart color when using the bar chart column.  This is a required
     * property when using the bar chart column.
     */
    metricColor: PropTypes.string,

    /**
     * Event handler for when the main view scrolls
     */
    onScroll: PropTypes.func,
});

export const Columns = PropTypes.arrayOf(Column);

export const Loader = PropTypes.func;

export const Sorter = PropTypes.exact({
    property: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
});

export const Sorters = PropTypes.arrayOf(Sorter);

export const Filter = PropTypes.exact({
    value: PropTypes.string.isRequired,
});

export const Filters = PropTypes.arrayOf(Filter);

export const LoadParams = PropTypes.object;

export const SelectionModel = PropTypes.exact({
    inverted: PropTypes.bool,
    selection: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
});

export const SelectionOptions = PropTypes.exact({
    /**
     * The selection mode to use
     *
     * Defaults to `SELECTIONMODE_NONE` which prevents any selection model from applying
     */
    mode: PropTypes.oneOf([SELECTIONMODE_MULTI, SELECTIONMODE_SINGLE, SELECTIONMODE_NONE]),

    /**
     * Whether or not to allow "auto select first row" for single model operation. By default, is false
     */
    needSingleAutoSelectFirst: PropTypes.bool,

    /**
     * If `true` forces at least one item to remain selected in the grid.
     *
     * Defaults to: `false`
     */
    force: PropTypes.bool,

    /**
     * Whether or not to allow the "Select All" feature, where all rows can be selected.
     * Behind the scenes, this enables a feature called selection inversion which is a flag
     * on whether or not a selection model has been inverted.
     */
    allowSelectAll: PropTypes.bool,

    /**
     * Whether or not to allow a "reselect" operation. By default, selecting an item which is
     * already selected will be treated as a "deselect" operation. By setting this to true, an
     * onChange event will be sent each time an item is selected (and it cannot be deselected).
     */
    allowReselect: PropTypes.bool,

    /**
     * The event handler for when a selection event takes place
     */
    onChange: PropTypes.func,

    /**
     * The field mapping to get the ID for a given row. This does NOT
     * use the lodash get. If you have nested fields, use `idMapper` instead
     *
     * Defaults to: `'id'`
     */
    idMapping: PropTypes.string,

    /**
     * The function to use to map a unique ID from a row. Default implementation
     * pulls the property defined in `idMapping`.
     */
    idMapper: PropTypes.func,
});

export const DataTablePropTypes = {
    /**
     * The ID of this table. This must be unique to any tables on a given page. This means
     * custom dashboards. Multiples of the same panel must yield reproducable, yet unique
     * IDs for each table.
     */
    tableId: PropTypes.string,

    /**
     * Shows the toolbar containing the row counts, search bar, etc...
     *
     * Defaults to: `true`
     */
    showToolbar: PropTypes.bool,

    /**
     * Shows the search text field to filter the data in the datatable.
     *
     * Defaults to: `true`
     */
    showSearch: PropTypes.bool,

    /**
     * Shows the headers for the datatable.
     *
     * Defaults to: `true`
     */
    showHeaders: PropTypes.bool,

    /**
     * Will auto-focus the search text field when true.
     *
     * Defaults to: `false`
     */
    autoFocusSearch: PropTypes.bool,

    /**
     * Shows a small, 1px loader at the top of the table when the datatable is loading
     *
     * Defaults to: `true`
     */
    showLoader: PropTypes.bool,

    /**
     * The maximum number of visible rows this table will display. If `0`, this table will
     * automatically size itself to it's parent container. You'll need ensure the container
     * is using a flex layout to grow the datatable. This is typically used for full-height
     * tables.
     *
     * Defaults to `10`
     */
    visibleRows: PropTypes.number,

    /**
     * Whether or not to automatically shrink the datatable if the number of rows
     * in the datatable is less than `visibleRows`. This behavior is not desireable
     * if the data in the table changes frequently. It will make anything under the table
     * move around as it fetches and displays data.
     *
     * Defaults to: `false`
     */
    shrink: PropTypes.bool,

    /**
     * Specifies the size of the rows in the datatable. One of small, medium, or large.
     *
     * Defaults to: `'medium'`
     */
    rowSize: PropTypes.oneOf(['small', 'medium', 'large', 'path-chart']),

    /**
     * If specified, this text is displayed in the datatable if there is not data to display
     *
     * Defaults to: `'No Data Found'`
     */
    emptyText: StringOrTranslatedTitle,

    /**
     * If specified, this text is displayed when the datatable is performing a "first" load
     *
     * Defaults to: `'Loading Data'`
     */
    loadingText: StringOrTranslatedTitle,

    /**
     * The callback function to load data for this table. If `null`, the table will not load anything.
     *
     *     function loaderFn(loadParams) {
     *         const {
     *              start, // the start row (int)
     *              limit, // the max number of rows to return (int)
     *              filters, // any filters ([{ value }])
     *              sorters, // any sorters ([{ property, direction}])
     *              params // andy additional params (see params)
     *          } = loadParams;
     *
     *         // loader function must return a Promise
     *         return Promise.resolve({
     *             rows: [], // the data rows to return
     *             length: 0 // the total number of results
     *         });
     *     }
     *
     * **Important**: This value must be memoized with `useCallback`. Every time
     * it changes, the data will be completely re-loaded. One would want to change
     * this value when we want to reset the dataset.
     */
    loader: PropTypes.func,

    /**
     * The sorters for this table
     *
     * **Important**: This value must be memoized with `useMemo`. Every time
     * it changes, the data will be completely re-loaded. One would want to change
     * this value when we want to reset the dataset by changing sorters.
     */
    sorters: Sorters,

    /**
     * The search value for this datatable
     */
    search: PropTypes.string,

    /**
     * Any additional parameters to include in the request made by the datatable.
     *
     * **Important**: This value must be memoized with `useMemo`. Every time
     * it changes, the data will be completely re-loaded. One would want to change
     * this value when we want to reset the dataset by changing filters.
     */
    params: LoadParams,

    /**
     * The number of rows per page to load a time.
     *
     * **Important**: If this changes, the data will be completely re-loaded as
     * page boundaries will have changed.
     *
     * Defaults to: `100`
     */
    limit: PropTypes.number,

    /**
     * The columns to display in this table. This is a required property!
     *
     * **Important**: This value must be memoized with `useMemo`. The datatable
     * will completely re-render everything when this value changes.
     */
    columns: Columns.isRequired,

    /**
     * A "sticky" search to apply to the data table
     */
    stickySearch: PropTypes.string,

    /**
     * The initial row selection. This is an object with a selection of IDs that are selected. An
     * optional `inverted` flag can be passed in for the use of select all.
     */
    selection: SelectionModel,

    /**
     * The options used to specify how selections are managed in this datatable
     */
    selectionOptions: SelectionOptions,

    /**
     * Whether or not to display a selection column, if selections are enabled. See: `selectionOptions`.
     */
    showSelectionColumn: PropTypes.bool,

    /**
     * The initial state to apply to this datatable. This is only applied when the table first initializes and
     * is ignored afterwards, unless the object reference changes again
     */
    state: PropTypes.object,

    /**
     * Callback to notify an external component that the datatable state has changed
     */
    onStateChanged: PropTypes.func,

    /**
     * The mapping to the true/false field which indicates if a row has been deleted or not
     */
    deletedMapping: PropTypes.string,

    /**
     * A ref to assign a reload function to the datatable. The function signature of this function is:
     *
     * @type (setter : (clearSelection = true : boolean, hardReset = false : boolean) => void)
     * @type { current: (clearSelection = true : boolean, hardReset = false : boolean) => void) }
     */
    triggerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

    /**
     * column overrides from page preferences or dashboard framework
     * those are the coluns that will be excluded from display on initial load
     * array of column objects format {id: title, hidden: true}
     */
    columnOverrides: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),

    /**
     * Whether or not to make use of sticky state (stored in sessionstorage) if this
     * dataTable has a `tableId`.
     *
     * Defaults to `true`.
     */
    enableStickyState: PropTypes.bool,
};

export const DataTableDefaultProps = {
    tableId: null,
    showToolbar: true,
    showSearch: true,
    showHeaders: true,
    autoFocusSearch: false,
    showLoader: true,
    rowSize: ROWSIZE.MEDIUM,
    emptyText: 'No data available',
    loadingText: 'Loading Data',
    limit: 100,
    loader: null,
    visibleRows: 10,
    shrink: true,
    params: null,
    sorters: null,
    search: null,
    stickySearch: null,
    selection: null,
    selectionOptions: null,
    showSelectionColumn: false,
    deletedMapping: 'deleted',
    onScroll: null,
    triggerRef: null,
    columnOverrides: null,
    enableStickyState: true,
};
